import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, mapTo, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { CachedPayload, CachedPOSTRequest } from '../classes/cached-request';
import { OnlineDetectorInjectionToken } from '../interfaces/online-detector.interface';
import { deleteCache, resetStore } from '../store/actions/hydration.actions';
import {
    addGetRequestsToQueue,
    refreshAllRequests,
    refreshPendingRequests,
    refreshRequest,
} from '../store/actions/refresh-request-queue.actions';
import { synchronizeQueue } from '../store/actions/request-queue.actions';
import { selectRehydrationCompletedState } from '../store/selectors/cache-info.selector';
import { getCachedResponseByUrl, isCacheEmpty } from '../store/selectors/cache.selector';
import {
    getRefreshRequestCount,
    hasGETErrorOccured,
    hasGETErrorOccurredForRequest,
    selectIsRefreshAllDone,
    selectIsRefreshDone,
} from '../store/selectors/refresh-request-queue.selector';
import {
    hasErroredPOSTRequest,
    hasNonErroredPOSTRequest,
    isRequestPending,
    pendingRequestCount,
    selectRequestQueue,
} from '../store/selectors/request-queue.selector';
import { PaceOfflineFeatureState } from '../store/state';
import { OfflineStrategy, OfflineStrategyInjectionToken } from '../interfaces/offline-strategy.interface';
import { UpgradableEncryption } from '@evorait/pace-encryption';

@Injectable()
export class OfflineService implements UpgradableEncryption {
    private isAppInitializedSub = new BehaviorSubject<boolean>(false);
    public isAppInitialized$ = this.isAppInitializedSub.asObservable();
    private isSyncingSub = new BehaviorSubject<boolean>(false);
    public isSyncing$ = this.isSyncingSub.asObservable().pipe(distinctUntilChanged());

    private requestSub = new Subject<string>();
    public request$ = this.requestSub.asObservable();
    private stateHydrationSub = new Subject<boolean>();
    public stateHydration$ = this.stateHydrationSub.asObservable();

    public isSyncRefreshCompleted$ = combineLatest([this.isSyncing$, this.isRefreshAllDone()]).pipe(
        map(([isSync, isRefreshDone]) => isSync || !isRefreshDone),
    );

    public isRequestPending$: Observable<boolean>;
    public pendingRequestCount$: Observable<number>;
    public selectRequestQueue$: Observable<CachedPOSTRequest[]>;
    public isRehydrationCompleted$: Observable<boolean>;
    private static readonly OFFLINE_PLUGIN_SYNC_ERROR = 'OFFLINE_PLUGIN_SYNC_ERROR';
    public constructor(
        @Inject(OnlineDetectorInjectionToken) public readonly isOnline$: Observable<boolean>,
        private store: Store<PaceOfflineFeatureState>,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(OfflineStrategyInjectionToken) private offlineStrategy: OfflineStrategy,
    ) {
        this.isRequestPending$ = this.store.select(isRequestPending);
        this.pendingRequestCount$ = this.store.select(pendingRequestCount);
        this.selectRequestQueue$ = this.store.select(selectRequestQueue);
        this.isRehydrationCompleted$ = this.store.select(selectRehydrationCompletedState);
    }

    /**
     *
     * Checks if there are any entries using legacy encryption format.
     *
     * This method queries the collection of stored entries and determines whether
     * any entry's content starts with the legacy encryption prefix.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` if at least one
     * entry uses legacy encryption, otherwise `false`.
     */
    public hasLegacyEncryption(): Observable<boolean> {
        return this.offlineStrategy.hasLegacyEncryption();
    }

    /**
     * Upgrades all entries from legacy encryption to the current encryption format.
     *
     * This method performs a read-write transaction on the collection of stored entries.
     * It finds all entries whose content starts with the legacy prefix,
     * re-encrypts their content using the `encryptionMigrator`, and updates the entries
     * in the database.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` once all legacy
     * encrypted entries have been successfully migrated.
     */
    public upgradeEncryption(): Observable<boolean> {
        return this.offlineStrategy.upgradeEncryption();
    }

    public setAppInitializationCompleted(val: boolean): void {
        this.isAppInitializedSub.next(val);
    }

    public setStateHydration(val: boolean): void {
        this.stateHydrationSub.next(val);
    }

    public setSyncing(sync: boolean): void {
        this.isSyncingSub.next(sync);
    }

    public setRequest(req: string): void {
        this.requestSub.next(req);
    }

    public sendOfflineData(): Observable<boolean> {
        this.store.dispatch(synchronizeQueue());
        return combineLatest([this.store.select(hasNonErroredPOSTRequest), this.isSyncing$]).pipe(
            // TODO rethink this. The naming is really confusing as it is relying on internal mechanisms
            filter(([isPending, isSyncing]) => !isPending && !isSyncing),
            take(1),
            mergeMap(() => this.store.select(hasErroredPOSTRequest).pipe(take(1))),
            mergeMap((error) => {
                if (error) {
                    throw new Error(OfflineService.OFFLINE_PLUGIN_SYNC_ERROR);
                } else {
                    return of(true);
                }
            }),
        );
    }

    public addPendingRequestsTextToLogs(): Observable<void> {
        return this.selectRequestQueue$.pipe(
            take(1),
            tap((requests) => {
                this.log.info(`OfflineService: RequestQueue count: ${requests.length}`);
                requests.forEach((request, index) =>
                    this.log.info(
                        `OfflineService: RequestQueue #${index}: url: ${request.url}, endpoint: ${
                            request.url.toLowerCase().indexOf('deepinsert') > 0
                                ? (<CachedPayload>request.body)?.Endpoint
                                : request.url
                        }, status: ${request.requestStatus}, created: ${request.createdAt ?? ''}, modified: ${
                            request.modifiedAt ?? ''
                        }, attempts: ${request.attempts ?? 0}`,
                    ),
                );
            }),
            mapTo(undefined),
        );
    }

    public getCachedValue(url: string): Observable<unknown> {
        return this.store.select(getCachedResponseByUrl, { url });
    }

    public registerSynchronizeTrigger(): Observable<boolean> {
        this.store.dispatch(synchronizeQueue());
        return this.isSyncing$.pipe(
            filter((offlineSyncInProgress) => !offlineSyncInProgress),
            take(1),
        );
    }

    public refreshAll(cacheFirst: boolean): Observable<boolean> {
        this.store.dispatch(refreshAllRequests({ cacheFirst }));
        return this.isSyncing$.pipe(
            filter((offlineSyncInProgress) => !offlineSyncInProgress),
            take(1),
            mergeMap(() =>
                this.store.select(isCacheEmpty).pipe(
                    take(1),
                    withLatestFrom(this.store.select(hasGETErrorOccured)),
                    mergeMap(([noCache, error]) => {
                        if (noCache && error) {
                            throw new Error(OfflineService.OFFLINE_PLUGIN_SYNC_ERROR);
                        }
                        return of(true);
                    }),
                ),
            ),
        );
    }

    public refreshRequest(definingRequestUrl: string): Observable<boolean> {
        this.store.dispatch(refreshRequest({ url: definingRequestUrl }));
        return this.isSyncing$.pipe(
            filter((offlineSyncInProgress) => !offlineSyncInProgress),
            take(1),
            mergeMap(() => this.store.select(hasGETErrorOccurredForRequest, { url: definingRequestUrl })),
            mergeMap((error) => {
                if (error) {
                    throw new Error(OfflineService.OFFLINE_PLUGIN_SYNC_ERROR);
                } else {
                    return this.isRefreshDone(definingRequestUrl);
                }
            }),
        );
    }

    public isRefreshDone(definingRequestUrl: string): Observable<boolean> {
        return this.store.select(selectIsRefreshDone, { url: definingRequestUrl });
    }

    public isRefreshAllDone(): Observable<boolean> {
        return this.store.select(selectIsRefreshAllDone);
    }

    public synchronize(): void {
        this.store.dispatch(refreshPendingRequests());
    }

    public deleteOfflineStore(): void {
        this.store.dispatch(deleteCache());
    }

    public resetOfflineStore(): void {
        this.store.dispatch(resetStore());
    }

    public addMultipleRefreshRequest(uriContent: string, requests: string[]): Observable<number> {
        this.store.dispatch(addGetRequestsToQueue({ uriContent, requests }));
        return this.store.select(getRefreshRequestCount);
    }
}
